import { create } from 'zustand';
import { DUNGEON_REGISTRY, DEFAULT_DUNGEON_ID } from '../data/dungeons.config';

const STORAGE_KEY = 'usagi_unlocked_dungeons';

interface UnlockedDungeonsState {
  /** Unlocked dungeon IDs (a set serialized as an array). */
  unlocked: string[];
  /** Fully completed dungeon IDs (a set serialized as an array). */
  completed: string[];
}

interface UnlockedDungeonsActions {
  /**
   * Called manually at the end of a dungeon, a quest, or any other contextual
   * trigger. Idempotent.
   */
  unlockDungeon: (dungeonId: string) => boolean;
  isDungeonUnlocked: (dungeonId: string) => boolean;
  /** Marks a dungeon as completed. Returns true if newly completed. */
  markCompleted: (dungeonId: string) => boolean;
  isCompleted: (dungeonId: string) => boolean;
  resetUnlocks: () => void;
}

function load(): UnlockedDungeonsState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<UnlockedDungeonsState>;
      return {
        unlocked: parsed.unlocked ?? [],
        completed: parsed.completed ?? [],
      };
    }
  } catch {
    /* ignore */
  }
  return { unlocked: [], completed: [] };
}

function persist(state: UnlockedDungeonsState): void {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ unlocked: state.unlocked, completed: state.completed }),
  );
}

export const useUnlockedDungeonsStore = create<
  UnlockedDungeonsState & UnlockedDungeonsActions
>((set, get) => ({
  ...load(),

  unlockDungeon: (dungeonId) => {
    if (get().unlocked.includes(dungeonId)) {
      return false;
    }
    set((s) => {
      const next = { ...s, unlocked: [...s.unlocked, dungeonId] };
      persist(next);
      return next;
    });
    return true;
  },

  isDungeonUnlocked: (dungeonId) => get().unlocked.includes(dungeonId),

  markCompleted: (dungeonId) => {
    if (get().completed.includes(dungeonId)) {
      return false;
    }
    set((s) => {
      const next = { ...s, completed: [...s.completed, dungeonId] };
      persist(next);
      return next;
    });
    return true;
  },

  isCompleted: (dungeonId) => get().completed.includes(dungeonId),

  resetUnlocks: () => {
    const reset = { unlocked: [], completed: [] };
    set(reset);
    persist(reset);
  },
}));

/**
 * At boot, ensures the starting dungeon is unlocked. Idempotent.
 */
export function seedDefaultUnlockedDungeons(): void {
  useUnlockedDungeonsStore.getState().unlockDungeon(DEFAULT_DUNGEON_ID);
}

export function seedAllUnlockedDungeons(): void {
  const store = useUnlockedDungeonsStore.getState();
  for (const id of Object.keys(DUNGEON_REGISTRY)) {
    store.unlockDungeon(id);
  }
}
